import { Component, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { Router, NavigationEnd, NavigationStart } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { localStorageKey, LOGOUT_MESSAGE } from "@core/constants";
import { SSOService } from "@core/services/sso.service";
import { environment as ENV } from "@environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { Subscription, interval, throwError, of } from "rxjs";
import { filter, switchMap, first, tap } from "rxjs/operators";
import { WebAdapterService } from "@core/web-adapter/web-adapter.service";
import { GlobalModalService } from "@app/shared/services/global-modal.service";

declare var gtag;

const getCleanQueryString = (searchString: string) => {
  return searchString.split("&").reduce(function (prev, curr, i, arr) {
    var p = curr.split("=");
    if (p[0]) {
      prev[decodeURIComponent(p[0])] = decodeURIComponent(p[1]);
    }
    return prev;
  }, {});
};

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
})
export class AppComponent implements OnInit, OnDestroy {
  subscription: Subscription;
  private navigateEvent: () => void;
  private mfRouteChangedEvent: () => void;
  constructor(
    private ssoService: SSOService,
    private authService: AuthService,
    private router: Router,
    private renderer: Renderer2,
    private waService: WebAdapterService,
    private globalModalService: GlobalModalService
  ) {
    // Setup Google Analytics
    const navEndEvents$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    );
    navEndEvents$.subscribe((event: NavigationEnd) => {
      gtag("config", "G-VEQ2RMTS2Z", {
        page_path: event.urlAfterRedirects,
      });
      window.dispatchEvent(
        new CustomEvent("container-route-changed", {
          detail: { pathname: event.urlAfterRedirects },
        })
      );
    });

    // Set redirect path on refresh
    this.subscription = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        const browserRefresh = !router.navigated;
        if (browserRefresh) {
          const searchString = window.location.search.substring(
            1,
            window.location.search.length
          );
          const searchObj = getCleanQueryString(searchString);
          const redirect = {
            path: window.location.pathname,
            query: searchObj,
          };
          if (redirect.path !== "/") {
            localStorage.setItem("redirect", JSON.stringify(redirect));
          }
        }
      } else if (event instanceof NavigationEnd) {
        this.waService.removeAllScripts();
        this.waService.removeAllStyles();
      }
    });
  }

  public isLoading = true;
  showNexusLoader = true;

  public navigateToStoredRedirect() {
    // Redirect to saved redirect URL if any, otherwise go home
    const redirectStorageData = localStorage.getItem("redirect");
    try {
      const redirectData = JSON.parse(redirectStorageData);
      if (redirectData?.path) {
        localStorage.removeItem("redirect");
        this.router.navigate([redirectData.path], {
          queryParams: redirectData.query,
        });
      } else {
        const path = window.location.pathname;
        this.router.navigate([path]);
      }
    } catch (e) {
      const path = window.location.pathname;
      this.router.navigate([path]);
    }
  }

  public logout() {
    localStorage.clear();
    this.authService.logout({
      logoutParams: { returnTo: ENV.auth0.redirectUri },
    });
  }

  public init() {
    // ADD global navigation from mf to others modules
    this.navigateEvent = this.renderer.listen("window", "navigate", (event) => {
      const { path, ...options } = event.detail;
      this.router.navigate([path], options);
    });

    // Sync route with mf
    this.mfRouteChangedEvent = this.renderer.listen(
      "window",
      "mf-route-changed",
      (event) => {
        const { pathname } = event.detail;
        this.router.navigateByUrl(pathname, { replaceUrl: true });
      }
    );
    if (
      window.location.pathname === "/" &&
      window.location.search.includes("error=")
    ) {
      const searchString = window.location.search.substring(1);
      const searchObj: any = getCleanQueryString(searchString);

      if (searchObj.error === "access_denied" || searchObj.error === "ERR001") {
        alert("No puedes iniciar sesión, contacta con tu administrador");
        this.logout();
      }
    }
    // Handle logout event (from outside router to avoid flickering; router works as fallback)
    if (window.location.pathname === "/logout") {
      alert(LOGOUT_MESSAGE);
      this.logout();
    } else {
      const storageData = { baseUrl: ENV.gateway.base_url };
      const existingData = localStorage.getItem(localStorageKey);
      if (existingData) {
        try {
          const parsed = JSON.parse(existingData);
          if (parsed?.user && parsed?.tokenClaims) {
          
            this.isLoading = false;
            this.renderer.removeClass(document.body, 'loading');
            
          }
        } catch (e) {
         
        }
      }
      
      return this.authService.idTokenClaims$.pipe(
     
        filter((token) => token?.__raw != null && token.__raw !== undefined && token.__raw !== ""),
        first(),
        switchMap((token) => {
          const ssoToken: string = token.__raw;
          const email: string = token?.email;
          
          if (!ssoToken) {
            this.logout();
            return throwError("Token is undefined");
          }
       
          const existingData = localStorage.getItem(localStorageKey);
          if (existingData) {
            try {
              const parsed = JSON.parse(existingData);
              if (parsed?.user && parsed?.tokenClaims) {
              
                parsed.tokenClaims = ssoToken;
                localStorage.setItem(localStorageKey, JSON.stringify(parsed));
                this.isLoading = false;
                this.renderer.removeClass(document.body, 'loading');
                this.navigateToStoredRedirect();
                return of(parsed.user);
              }
            } catch (e) {
           
            }
          }
          
          storageData["tokenClaims"] = ssoToken;
          return this.ssoService.fetchUserData(ssoToken).pipe(
            tap((userData) => {
              storageData["user"] = userData;
              storageData["dataUser"] = userData;
              localStorage.setItem(
                localStorageKey,
                JSON.stringify(storageData)
              );
              window.parent.postMessage({ type: "FINISH_LOAD_USER_DATA" }, "*");
              localStorage.removeItem("validUser");
              this.navigateToStoredRedirect();
              this.isLoading = false;
              this.renderer.removeClass(document.body, 'loading');
            })
          );
        })
      ).subscribe(
        () => {
          // Éxito - ya se manejó en el tap
        },
        (error) => {
          // No user data, redirect to self onboarding
          /*
          const emptyUserData = { permissions: [], email };
          storageData["user"] = emptyUserData;
          storageData["dataUser"] = emptyUserData;
          localStorage.setItem(
            localStorageKey,
            JSON.stringify(storageData)
          );
          this.router.navigate(["/self-onboarding"]);
          */
          if (error !== "Token is undefined") {
            this.router.navigate(["/home"]);
            localStorage.setItem("validUser", "false");
          }
          this.isLoading = false;
          this.renderer.removeClass(document.body, 'loading');
        }
      );
    }
  }

  scheduleTokenRefresh() {
    const REFRESH_INTERVAL = 8 * 60 * 60 * 1000; // get a new token every 8 hours
    interval(REFRESH_INTERVAL)
      .pipe(
        switchMap(() =>
          this.authService.getAccessTokenSilently({ cacheMode: "off" })
        )
      )
      .subscribe(
        (token) => {
          const storageData = JSON.parse(localStorage.getItem(localStorageKey));
          storageData.tokenClaims = token;
          localStorage.setItem(localStorageKey, JSON.stringify(storageData));
        },
        (error) => {
          console.error("Error refreshing token", error);
        }
      );
  }

  saveUrlAndParams(): void {
    const route = window.location.href;
    if (route.split("/dashboard/").length > 1) {
      const queryParams = new URLSearchParams(
        route.split("/dashboard/")[1].split("?")[1]
      );
      const result: { [key: string]: string | string[] } = {};
      queryParams?.forEach((value, key) => {
        if (result[key]) {
          if (Array.isArray(result[key])) {
            (result[key] as string[]).push(value);
          } else {
            result[key] = [result[key] as string, value];
          }
        } else {
          result[key] = value;
        }
      });
      const redirectToPage = {
        redirect: true,
        url: `dashboard/${route.split("/dashboard/")[1]?.split("?")[0]}`,
        queryParams: result,
      };
      localStorage.setItem("redirect_to_page", JSON.stringify(redirectToPage));
    }
  }

  async ngOnInit() {
    this.renderer.addClass(document.body, 'loading');
    this.init();
    this.scheduleTokenRefresh();
    this.saveUrlAndParams();
  }

  ngOnDestroy(): void {
    this.navigateEvent && this.navigateEvent();
    this.mfRouteChangedEvent && this.mfRouteChangedEvent();
  }
}
